import React from "react";
import {IVendor, IVendorProductDetail} from "../../models/Search";
import CurrencyFormat from "react-currency-format";
import {InStockDisplay} from "../Products/InStockDisplay";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {ColumnGenerator} from "../../logic/Columns/ColumnGenerator";
import {DetailsList, Icon, SelectionMode, Spinner, TooltipHost, IconButton} from "@fluentui/react";
import { useGetAllVendorSettingsQuery } from "../../store/Api";
import { renderVendorNameFromAllSettings } from "../../logic/Product";
import MarkdownView from "react-showdown";
import useSubOrganizationScope from "../../hooks/useSubOrganizationScope";
import SubOrganizationBadge from "../SubOrganization/SubOrganizationBadge";
import SearchFilter from "../Common/SearchFilter";

interface IVendorsTableProps {
    vendors: IVendor[];
    refreshing?: boolean;
    product: INormalizedProduct;
    editable?: boolean;
}

export const VendorsTable: React.FC<IVendorsTableProps> = (props) => {
    const vendorSettings = useGetAllVendorSettingsQuery();
    const subOrganizationScope = useSubOrganizationScope();
    const columns = [
        ColumnGenerator('Vendor',
            'vendorName',
            150,
            150,
            (vendor: IVendor) => {
                if (vendor.subVendorName && vendor.subVendorName !== "") return <>{renderVendorNameFromAllSettings(vendor.vendorName, vendorSettings.data)} - {vendor.subVendorName}</>
                return <>{renderVendorNameFromAllSettings(vendor.vendorName, vendorSettings.data)}</>
            }
            ),
        ColumnGenerator('Name',
            'name',
            200,
            undefined,
            (vendor: IVendor) => {
                const vendorDetail = props.product.vendorDetails?.find(v => v.vendorName === vendor.vendorName && v.vendorPartNumber === vendor.vendorPartNumber && v.subVendorName === vendor.subVendorName);
                return <VendorNameDisplay vendor={vendor} detail={vendorDetail} />
            },
            true),
        ColumnGenerator('VPN', 'vendorPartNumber', 100, 100),
        // Deep link into the source system's own page for this product, when the import captured one.
        ColumnGenerator('', 'url', 36, 36, (vendor: IVendor) => vendor.url
            ? <TooltipHost content={`Open at ${renderVendorNameFromAllSettings(vendor.vendorName, vendorSettings.data)}`}>
                <IconButton
                    iconProps={{iconName: 'NavigateExternalInline'}}
                    ariaLabel='Open at the vendor'
                    href={vendor.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    styles={{root: {height: 24, width: 24}, icon: {fontSize: 12}}}
                />
            </TooltipHost>
            : <></>),
        ColumnGenerator('Price',
            'price',
            100,
            100,
            (vendor: IVendor) =>
                vendor.cost ? <CurrencyFormat
                        value={vendor.cost}
                        displayType="text"
                        thousandSeparator=","
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                    />
                    : <></>
        ),
    ]

    // Only worth a column when something is actually customer priced, which only happens under a
    // sub-organization scope.
    if (props.product?.vendors?.some(v => v.subOrganizationPricing)) {
        columns.push(ColumnGenerator('Pricing',
            'subOrganizationPricing',
            120,
            120,
            (vendor: IVendor) => vendor.subOrganizationPricing
                ? <SubOrganizationBadge
                    name={subOrganizationScope.selectedName ?? 'Customer pricing'}
                    tooltip={`Priced from ${subOrganizationScope.selectedName ?? 'this customer'}'s own price file.`}
                />
                : <></>
        ));
    }

    if (props.product?.vendors?.find(v => v.startQuantity || v.endQuantity)) {
        columns.push(ColumnGenerator('Start Quantity', 'startQuantity', 100, 100));
        columns.push(ColumnGenerator('End Quantity', 'endQuantity', 100, 100));
    }
    columns.push(
        ColumnGenerator('Status', 'status', 50, 50, (vendor: IVendor) => {
                if (props.refreshing) return <Spinner />
                return (vendor.status != null)
                    ? <div style={{textAlign: 'center'}}>
                        <TooltipHost content={vendor.status}>
                            <Icon iconName={vendor.status.toLowerCase().indexOf("authorized") !== -1 ? 'Warning' : 'Info'}/>
                        </TooltipHost>
                    </div>
                    : <></>;
            }
        )
    )
    columns.push(
        ColumnGenerator(
            'Availability',
            'inStock',
            200,
            200,
            (vendor: IVendor) => <>
                <InStockDisplay
                    vendor={vendor}
                    product={props.product}
                    loadingWarehouses={!!props.refreshing}
                />
            </>
        )
    )
    if (props.product.vendors?.find(v =>v.url !== null || "")){
         columns.push(
           ColumnGenerator(
               'Link',
               'url',
               50,
               50,
               (vendor: IVendor) => {
                   return (vendor.url && vendor.url !== '') ?
                           <IconButton
                               href={`${vendor.url}`}
                               iconProps={{iconName: 'Link'}}
                               target={'_blank'}
                           />
                       : <></>;    
               }
           )
         )    
    }
   
    const vendors = props.product?.vendors ?? [];

    // Under a customer scope the API returns only that customer's vendor rows, so an empty list means
    // "we have no price file data for them", not "this product has no vendors".
    if (vendors.length === 0 && subOrganizationScope.isScoped) {
        return <div style={{padding: '1em'}}>
            No pricing for {subOrganizationScope.selectedName} yet. Switch the customer scope back to
            All customers to see the organization's vendor pricing for this product.
        </div>;
    }

    return <DetailsList items={vendors} selectionMode={SelectionMode.none} columns={columns}/>
};


// Read-only pills styled like the search page's attribute filters, e.g. the billing/commitment terms that
// distinguish rows sharing a VPN.
const VendorAttributePills: React.FC<{vendor: IVendor}> = (props) => {
    const attributes = Object.entries(props.vendor.vendorAttributes ?? {}).filter(([, value]) => value != null && value !== '');
    if (attributes.length === 0) return null;
    return <div className="vendor-attribute-pills">
        {attributes.map(([name, value]) =>
            <SearchFilter key={name} protected label={`${name}: ${value}`}>{name}: {value}</SearchFilter>
        )}
    </div>;
}

interface IVendorNameDisplayProps {
    vendor: IVendor;
    detail?: IVendorProductDetail;
}

const VendorNameDisplay: React.FC<IVendorNameDisplayProps> = (props) => {
    const [expanded, setExpanded] = React.useState(false);

    const toggleExpanded = () => {
        setExpanded(!expanded);
    }
    
    if (!props.detail || !props.detail.description) return <>
        <div>{props.detail?.name ?? props.vendor.name}</div>
        <VendorAttributePills vendor={props.vendor} />
    </>

    return <>
        <div>{props.detail?.name ?? props.vendor.name}</div>
        <VendorAttributePills vendor={props.vendor} />
        <div style={{marginTop: '1em', color: '#007BFF', cursor: 'pointer'}} onClick={toggleExpanded}>{expanded ? 'Hide Description' : 'Show Description'}</div>
        {expanded && <MarkdownView markdown={props.detail.description} />}
    </>
}